import { useState, useMemo } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useCalendarData } from "@/hooks/useCalendarData";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle, Package, Users } from "lucide-react";
import { DayDetailPanel, ConflictDetailsCard } from "@/components/calendar/CalendarPanels";

const COLORS = ["bg-amber-500/30 border-amber-500/50 text-amber-200", "bg-blue-500/30 border-blue-500/50 text-blue-200", "bg-emerald-500/30 border-emerald-500/50 text-emerald-200", "bg-purple-500/30 border-purple-500/50 text-purple-200", "bg-rose-500/30 border-rose-500/50 text-rose-200", "bg-cyan-500/30 border-cyan-500/50 text-cyan-200"];
const DOT_COLORS = ["bg-amber-400", "bg-blue-400", "bg-emerald-400", "bg-purple-400", "bg-rose-400", "bg-cyan-400"];

function getWeekDates(date) {
  const d = new Date(date);
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const dd = new Date(start);
    dd.setDate(start.getDate() + i);
    days.push(dd);
  }
  return days;
}

function getMonthDates(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const days = [];
  // pad start
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }
  for (let i = 1; i <= last.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }
  // pad end
  while (days.length % 7 !== 0) {
    const d = new Date(year, month + 1, days.length - last.getDate() - startDay + 1);
    days.push({ date: d, isCurrentMonth: false });
  }
  return days;
}

function formatDateStr(d) {
  return d.toISOString().split('T')[0];
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CalendarPage() {
  const { role } = useOutletContext();
  const navigate = useNavigate();
  const { events, conflicts, gear, crew, gearMap, crewMap, loading } = useCalendarData(role);
  const [view, setView] = useState("month"); // "month" or "week"
  const [currentDate, setCurrentDate] = useState(new Date());

  // Events by date — filter out declined/cancelled
  const activeEvents = useMemo(() => events.filter(ev => ev.status !== "declined" && ev.status !== "cancelled"), [events]);
  
  // Map event_id to color index
  const eventColorMap = useMemo(() => {
    const map = {};
    activeEvents.forEach((ev, i) => { map[ev.event_id] = i % COLORS.length; });
    return map;
  }, [activeEvents]);

  // Events by date — deduplicate multi-day events (only show once per day)
  const eventsByDate = useMemo(() => {
    const map = {};
    activeEvents.forEach(ev => {
      if (!ev.start_date) return;
      const start = new Date(ev.start_date + "T00:00:00");
      const end = ev.end_date ? new Date(ev.end_date + "T00:00:00") : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = formatDateStr(d);
        if (!map[key]) map[key] = [];
        // Don't add duplicate event_id on same day
        if (!map[key].some(e => e.event_id === ev.event_id)) {
          map[key].push(ev);
        }
      }
    });
    return map;
  }, [activeEvents]);

  const conflictDates = useMemo(() => {
    const dates = new Set();
    conflicts.forEach(c => {
      const s1 = new Date(c.event1.start_date + "T00:00:00");
      const e1 = new Date(c.event1.end_date + "T00:00:00");
      const s2 = new Date(c.event2.start_date + "T00:00:00");
      const e2 = new Date(c.event2.end_date + "T00:00:00");
      const overlapStart = new Date(Math.max(s1, s2));
      const overlapEnd = new Date(Math.min(e1, e2));
      for (let d = new Date(overlapStart); d <= overlapEnd; d.setDate(d.getDate() + 1)) {
        dates.add(formatDateStr(d));
      }
    });
    return dates;
  }, [conflicts]);

  const [selectedDay, setSelectedDay] = useState(null);
  const todayStr = formatDateStr(new Date());
  const getGearName = (id) => gear.find(g => g.gear_id === id)?.name || id;
  const getCrewName = (id) => crew.find(c => c.crew_id === id)?.name || id;

  const prevPeriod = () => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };
  const nextPeriod = () => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };
  const goToday = () => setCurrentDate(new Date());

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate.getFullYear(), currentDate.getMonth());

  const periodLabel = view === "month"
    ? `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    : `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="calendar-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">Schedule overview with conflict detection</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="cal-today-btn" variant="secondary" size="sm" className="text-xs" onClick={goToday}>Today</Button>
          <div className="flex items-center border border-border rounded-sm overflow-hidden">
            <Button data-testid="cal-view-month" variant={view === "month" ? "default" : "ghost"} size="sm" className="text-xs rounded-none h-8" onClick={() => setView("month")}>Month</Button>
            <Button data-testid="cal-view-week" variant={view === "week" ? "default" : "ghost"} size="sm" className="text-xs rounded-none h-8" onClick={() => setView("week")}>Week</Button>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between">
        <Button data-testid="cal-prev" variant="ghost" size="icon" className="h-8 w-8" onClick={prevPeriod}><ChevronLeft className="w-4 h-4" /></Button>
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{periodLabel}</h2>
        <Button data-testid="cal-next" variant="ghost" size="icon" className="h-8 w-8" onClick={nextPeriod}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {conflicts.length > 0 && (
        <div data-testid="calendar-conflicts-banner" className="bg-red-500/10 border border-red-500/30 rounded-sm p-2.5 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
          <span className="text-xs text-red-300">{conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} — red borders indicate overlapping resources</span>
        </div>
      )}

      {/* MONTH VIEW */}
      {view === "month" && (
        <Card className="border-border overflow-hidden">
          <CardContent className="p-0">
            {/* Header */}
            <div className="grid grid-cols-7 border-b border-border">
              {DAY_NAMES.map(d => (
                <div key={d} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center py-2 border-r border-border last:border-r-0">{d}</div>
              ))}
            </div>
            {/* Days */}
            <div className="grid grid-cols-7">
              {monthDates.map(({ date, isCurrentMonth }, idx) => {
                const ds = formatDateStr(date);
                const dayEvents = eventsByDate[ds] || [];
                const isToday = ds === todayStr;
                const hasConflict = conflictDates.has(ds);
                const isSelected = ds === selectedDay;
                return (
                  <div
                    key={formatDateStr(date)}
                    className={`min-h-[120px] border-r border-b border-border last:border-r-0 p-1.5 transition-colors cursor-pointer hover:bg-secondary/20 
                      ${!isCurrentMonth ? 'bg-secondary/10' : 'bg-card'} 
                      ${hasConflict ? 'bg-red-500/5' : ''} 
                      ${isSelected ? 'ring-2 ring-primary ring-inset' : ''}`}
                    onClick={() => setSelectedDay(ds === selectedDay ? null : ds)}
                  >
                    <div className={`text-xs font-medium mb-0.5 px-1 ${isToday ? 'bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center' : isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map(ev => (
                        <div
                          key={ev.event_id}
                          data-testid={`month-event-${ev.event_id}-${ds}`}
                          className={`text-xs px-1.5 py-0.5 rounded-sm border cursor-pointer truncate ${COLORS[eventColorMap[ev.event_id]]} hover:opacity-80 transition-opacity`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const tab = ev.status === 'pending' ? 'pending' : ev.status === 'declined' ? 'declined' : ev.status === 'completed' ? 'archive' : 'upcoming';
                            navigate('/events', { state: { highlightEvent: ev.event_id, eventTab: tab } });
                          }}
                          title={`${ev.name}${ev.start_date !== ev.end_date ? ` (${ev.start_date} — ${ev.end_date})` : ''}`}
                        >
                          {ev.name}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div 
                          className="text-xs text-primary font-medium px-1 cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDay(ds);
                          }}
                        >
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* WEEK VIEW */}
      {view === "week" && (
        <Card className="border-border overflow-hidden">
          <CardContent className="p-0">
            {/* Header */}
            <div className="grid grid-cols-7 border-b border-border">
              {weekDates.map((d) => {
                const ds = formatDateStr(d);
                const isToday = ds === todayStr;
                return (
                  <div key={`wh-${ds}`} className={`text-center py-3 border-r border-border last:border-r-0 ${isToday ? 'bg-primary/10' : ''}`}>
                    <div className="text-xs text-muted-foreground uppercase">{DAY_NAMES[d.getDay()]}</div>
                    <div className={`text-lg font-bold mt-0.5 ${isToday ? 'text-primary' : ''}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
            {/* Events */}
            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDates.map((d) => {
                const ds = formatDateStr(d);
                const dayEvents = eventsByDate[ds] || [];
                const hasConflict = conflictDates.has(ds);
                return (
                  <div key={`wb-${ds}`} className={`border-r border-border last:border-r-0 p-1.5 space-y-1.5 ${hasConflict ? 'bg-red-500/5' : ''}`}>
                    {dayEvents.map(ev => (
                      <div
                        key={ev.event_id}
                        data-testid={`week-event-${ev.event_id}-${ds}`}
                        className={`p-2 rounded-sm border cursor-pointer ${COLORS[eventColorMap[ev.event_id]]} hover:opacity-80 transition-opacity`}
                        onClick={() => {
                          const tab = ev.status === 'pending' ? 'pending' : ev.status === 'declined' ? 'declined' : ev.status === 'completed' ? 'archive' : 'upcoming';
                          navigate('/events', { state: { highlightEvent: ev.event_id, eventTab: tab } });
                        }}
                      >
                        <div className="text-xs font-semibold truncate">{ev.name}</div>
                        <div className="text-xs opacity-70 truncate mt-0.5">{ev.venue || ''}</div>
                        <div className="flex items-center gap-2 mt-1 text-xs opacity-60">
                          <span className="flex items-center gap-0.5"><Package className="w-2.5 h-2.5" />{ev.gear_assignments?.length || 0}</span>
                          <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{ev.crew_assignments?.length || 0}</span>
                        </div>
                      </div>
                    ))}
                    {dayEvents.length === 0 && (
                      <div className="text-xs text-muted-foreground/30 text-center pt-4">—</div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <DayDetailPanel
        selectedDay={selectedDay}
        events={eventsByDate[selectedDay] || []}
        colorMap={eventColorMap}
        COLORS={COLORS}
        onClose={() => setSelectedDay(null)}
      />

      <ConflictDetailsCard
        conflicts={conflicts}
        getCrewName={getCrewName}
      />
    </div>
  );
}
